import { ApiClient, ApiError } from "@/lib/api-sdk";
import type { OpenAPIConfig } from "@/lib/api-sdk/core/OpenAPI";
import { useAuthStore } from "@/stores/auth-store";

// =============================================================================
// Authenticated API Client Wrapper
// =============================================================================

/**
 * Wrapper around the generated SDK ApiClient that adds:
 * - Automatic JWT token injection
 * - Automatic token refresh on 401 errors
 * - Logout on refresh failure
 */
class AuthenticatedApiClient {
	private client: ApiClient;
	private isRefreshing = false;
	private refreshPromise: Promise<boolean> | null = null;

	constructor(baseUrl: string) {
		const config: Partial<OpenAPIConfig> = {
			BASE: baseUrl,
			// TOKEN is a resolver function that returns the token dynamically
			TOKEN: async () => {
				const token = useAuthStore.getState().token;
				return token ?? "";
			},
		};

		this.client = new ApiClient(config);
	}

	/**
	 * Attempts to refresh the access token using the refresh token
	 * @returns true if refresh succeeded, false otherwise
	 */
	private async refreshAccessToken(): Promise<boolean> {
		// Prevent multiple simultaneous refresh attempts
		if (this.isRefreshing && this.refreshPromise) {
			return this.refreshPromise;
		}

		this.isRefreshing = true;
		this.refreshPromise = (async () => {
			try {
				const refreshToken = localStorage.getItem("refreshToken");
				if (!refreshToken) {
					useAuthStore.getState().logout();
					return false;
				}

				const userAgent = navigator.userAgent || "Unknown";
				const response = await this.client.auth.authControllerRefresh({
					userAgent,
					requestBody: { refreshToken },
				});

				// Update tokens in store (refresh doesn't return user, keep existing)
				// Response format: { data: { accessToken, refreshToken, expiresAt } }
				const { accessToken, refreshToken: newRefreshToken } = response.data;
				const currentUser = useAuthStore.getState().user;
				if (currentUser) {
					useAuthStore.getState().login(currentUser, accessToken);
				}
				localStorage.setItem("refreshToken", newRefreshToken);

				return true;
			} catch {
				// Refresh failed, logout user
				useAuthStore.getState().logout();
				localStorage.removeItem("refreshToken");
				return false;
			} finally {
				this.isRefreshing = false;
				this.refreshPromise = null;
			}
		})();

		return this.refreshPromise;
	}

	/**
	 * Wraps an API call with automatic retry on 401 after token refresh
	 */
	private async withTokenRefresh<T>(apiCall: () => Promise<T>): Promise<T> {
		try {
			return await apiCall();
		} catch (error) {
			// If we got a 401 error, try to refresh the token and retry
			if (error instanceof ApiError && error.status === 401) {
				const refreshed = await this.refreshAccessToken();
				if (refreshed) {
					// Token refreshed successfully, retry the original request
					return await apiCall();
				}
			}
			// Re-throw the error if it's not a 401 or refresh failed
			throw error;
		}
	}

	/**
	 * Get the underlying SDK client with automatic token refresh on 401
	 */
	get services() {
		// We can't easily wrap every method automatically, so we'll expose the client
		// and let the hooks handle the 401 retry logic using withTokenRefresh
		return this.client;
	}

	/**
	 * Execute an API call with automatic token refresh on 401
	 * Usage: apiSdk.withRefresh(() => apiSdk.services.assets.someMethod())
	 */
	async withRefresh<T>(apiCall: () => Promise<T>): Promise<T> {
		return this.withTokenRefresh(apiCall);
	}
}

// =============================================================================
// Exported Instance
// =============================================================================

/**
 * Authenticated API SDK client instance
 *
 * Usage in hooks:
 * ```typescript
 * const result = await apiSdk.withRefresh(() =>
 *   apiSdk.services.auth.authControllerGetMe()
 * );
 * ```
 */
export const apiSdk = new AuthenticatedApiClient(
	import.meta.env.VITE_API_URL ||
		import.meta.env.VITE_API_BASE_URL?.replace(/\/api$/, "") ||
		"http://localhost:3000",
);

// Re-export ApiError for error handling
export { ApiError } from "@/lib/api-sdk";
